import { BookOpenCheck, Clock3, Compass } from "lucide-react";

import type { Domain, ParentReport } from "@/domain";

const DOMAIN_LABELS: Record<Domain, string> = {
  chinese: "语文",
  math: "数学",
  english: "英语",
  habits: "生活习惯",
  "focus-social": "专注与交往"
};

const DATE_FORMATTER = new Intl.DateTimeFormat("zh-CN", {
  year: "numeric",
  month: "numeric",
  day: "numeric"
});

export interface ParentReportViewProps {
  report: ParentReport;
}

export function ParentReportView({ report }: ParentReportViewProps) {
  return (
    <div className="parent-center__report">
      <section
        aria-labelledby="parent-overview-title"
        className="parent-center__report-section"
      >
        <div className="parent-center__section-heading">
          <div>
            <p className="parent-center__eyebrow">学习报告</p>
            <h2 id="parent-overview-title">
              {report.profile.nickname} 的练习概览
            </h2>
          </div>
        </div>
        <dl className="parent-center__summary">
          <div>
            <Clock3 aria-hidden="true" />
            <dt>累计练习</dt>
            <dd>{formatMinutes(report.practicedMinutes)}</dd>
          </div>
          <div>
            <BookOpenCheck aria-hidden="true" />
            <dt>近期完成</dt>
            <dd>{report.recentAttempts.length} 项</dd>
          </div>
          <div>
            <Compass aria-hidden="true" />
            <dt>建议关注</dt>
            <dd>
              {report.suggestedDomains
                .map((domain) => DOMAIN_LABELS[domain])
                .join("、")}
            </dd>
          </div>
        </dl>
      </section>

      <section
        aria-labelledby="parent-mastery-title"
        className="parent-center__report-section"
      >
        <div className="parent-center__section-heading">
          <h2 id="parent-mastery-title">各岛掌握情况</h2>
        </div>
        {report.mastery.length === 0 ? (
          <p className="parent-center__empty-copy">完成活动后会显示掌握情况。</p>
        ) : (
          <div className="parent-center__table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">学习领域</th>
                  <th scope="col">掌握程度</th>
                  <th scope="col">建议难度</th>
                  <th scope="col">更新时间</th>
                </tr>
              </thead>
              <tbody>
                {report.mastery.map((entry) => (
                  <tr key={entry.domain}>
                    <th scope="row">{DOMAIN_LABELS[entry.domain]}</th>
                    <td>{formatPercent(entry.score)}</td>
                    <td>第 {entry.recommendedDifficulty} 阶</td>
                    <td>
                      <time dateTime={entry.updatedAt}>
                        {formatDate(entry.updatedAt)}
                      </time>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section
        aria-labelledby="parent-recent-title"
        className="parent-center__report-section"
      >
        <div className="parent-center__section-heading">
          <h2 id="parent-recent-title">最近练习</h2>
        </div>
        {report.recentAttempts.length === 0 ? (
          <p className="parent-center__empty-copy">还没有已完成的练习。</p>
        ) : (
          <div className="parent-center__table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">学习领域</th>
                  <th scope="col">难度</th>
                  <th scope="col">正确率</th>
                  <th scope="col">提示 / 重试</th>
                  <th scope="col">用时</th>
                  <th scope="col">完成日期</th>
                </tr>
              </thead>
              <tbody>
                {report.recentAttempts.map((attempt) => (
                  <tr key={attempt.id}>
                    <th scope="row">{DOMAIN_LABELS[attempt.domain]}</th>
                    <td>第 {attempt.difficulty} 阶</td>
                    <td>{formatPercent(attempt.correctRate)}</td>
                    <td>
                      {attempt.hintCount} / {attempt.retryCount}
                    </td>
                    <td>{formatDuration(attempt.durationMs)}</td>
                    <td>
                      <time dateTime={attempt.completedAt}>
                        {formatDate(attempt.completedAt)}
                      </time>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function formatMinutes(minutes: number): string {
  if (minutes > 0 && minutes < 1) {
    return "不足 1 分钟";
  }

  return (
    minutes.toLocaleString("zh-CN", {
      maximumFractionDigits: 1
    }) + " 分钟"
  );
}

function formatPercent(value: number): string {
  const normalizedValue = Math.min(1, Math.max(0, value));
  return Math.round(normalizedValue * 100) + "%";
}

function formatDuration(durationMs: number): string {
  const seconds = Math.max(0, Math.round(durationMs / 1_000));
  if (seconds < 60) {
    return seconds + " 秒";
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds === 0
    ? minutes + " 分钟"
    : minutes + " 分 " + remainingSeconds + " 秒";
}

function formatDate(isoTimestamp: string): string {
  return DATE_FORMATTER.format(new Date(isoTimestamp));
}
